import { TicketSalePageEvent } from 'app/pages/TicketSalePage/slice/types';
import { DateTime } from 'luxon';
import {
  isSeat,
  SelectedBundle,
  SelectedSeat,
  SelectedTicket,
} from 'types/SelectedItem';
import { dateFormatForCalendar, getTimetableForTicket } from 'utils/calendar';
import { EVENT_TYPE_WITH_CALENDAR } from 'utils/static/constants';
import { OrderedTickets } from './types';
import { bundleHasCalendar, getTicketTypes } from 'utils/bundles';

export interface IFormattedTicketForReservation {
  id: string;
  quantity: number;
  category_key?: string;
  seat_id?: string;
  seat_label?: string;
  id_ticket_subtype?: string;
  idTimetable?: string;
  date?: string;
}

export const formatSelectedTicketsForReservationEndpoint = (
  selectedTickets: (SelectedTicket | SelectedSeat)[],
  selectedEvent: TicketSalePageEvent,
) =>
  selectedTickets.map(ticket => {
    const item: IFormattedTicketForReservation = {
      id: ticket.ticketId,
      quantity: ticket.quantity,
    };

    if (isSeat(ticket)) {
      item.category_key = selectedEvent.tickets.find(
        ({ id }) => id === ticket.ticketId,
      )?.seatsCategoryKey;
      item.seat_id = ticket.seatId;
      item.seat_label = ticket.seatLabel;
      if (ticket.subtypeId) {
        item.id_ticket_subtype = ticket.subtypeId;
      }
    }

    if (ticket.date && selectedEvent.type === EVENT_TYPE_WITH_CALENDAR) {
      item.date = ticket.date;
      item.idTimetable = getTimetableForTicket(
        selectedEvent.calendar!,
        DateTime.fromFormat(ticket.date, dateFormatForCalendar),
        ticket.ticketId,
      ).timetableId;
    }

    return item;
  });

export interface IFormattedBundleForReservation {
  id: string;
  quantity: number;
  date?: string;
  timetables?: {
    ticketTypeId: string;
    timetableId: string;
  }[];
}

export const formatSelectedBundlesForReservationEndpoint = (
  selectedBundles: SelectedBundle[],
  selectedEvent: TicketSalePageEvent,
) => {
  return selectedBundles.map(({ id, quantity, date }) => {
    const item: IFormattedBundleForReservation = {
      id,
      quantity,
    };

    if (date && selectedEvent.type === EVENT_TYPE_WITH_CALENDAR) {
      item.date = date;
      const bundleData = selectedEvent.ticketTypeBundles!.find(
        el => el.id === id,
      );
      item.timetables = bundleData!.items.map(item => ({
        ticketTypeId: item.idTicketType,
        timetableId: getTimetableForTicket(
          selectedEvent.calendar!,
          DateTime.fromFormat(date, dateFormatForCalendar),
          item.idTicketType,
        ).timetableId,
      }));
    }

    return item;
  });
};

export const getIdOrderItemForTicket = (
  selectedTicket: SelectedTicket | SelectedSeat,
  selectedEvent: TicketSalePageEvent,
  orderedTickets: OrderedTickets[],
) =>
  orderedTickets.find(orderItem => {
    if (orderItem.bundleId) return false;
    if (
      selectedEvent.tickets.find(el => el.id === selectedTicket.ticketId)
        ?.hasCalendar &&
      orderItem.date !== selectedTicket.date
    )
      return false;
    return orderItem.ticketTypeId === selectedTicket.ticketId;
  })!.id; // we are using Non-null Assertion Operator because we are sure that selectedTicket is in the orderedTickets.
// there is only one case when the ticket won't be inside orderedTickets - if the selectedTicket if from anoter event
// but we covered this case in fetchEventWithTickets action where we remove all the selectedItems from other events
// after page data is fetched

export const getOrderItemsForBundle = (
  selectedBundle: SelectedBundle,
  selectedEvent: TicketSalePageEvent,
  orderedTickets: OrderedTickets[],
) =>
  orderedTickets
    .filter(orderItem => {
      if (orderItem.bundleId !== selectedBundle.id) return false;

      const bundleData = selectedEvent.ticketTypeBundles?.find(
        el => el.id === selectedBundle.id,
      );
      const ticketTypes = getTicketTypes(
        bundleData!.items,
        selectedEvent.tickets,
      );
      if (
        bundleHasCalendar(ticketTypes) &&
        orderItem.date !== selectedBundle.date
      )
        return false;

      return orderItem.bundleId === selectedBundle.id;
    })
    .map(orderItem => ({
      id: orderItem.id,
      idTicketType: orderItem.ticketTypeId,
    }));
